/**
 * MicManager - Centralized microphone and AudioContext management
 * 
 * Singleton that manages microphone access, MediaStream caching, and AudioContext lifecycle.
 * Prevents null MediaStream errors and AudioContext chaos by providing a single source of truth.
 */

class MicManager {
    constructor() {
        if (MicManager.instance) {
            return MicManager.instance;
        }

        // Singleton instance
        MicManager.instance = this;

        // State
        this.mediaStream = null;
        this.audioContext = null;
        this.isAcquired = false;
        this.acquisitionPromise = null;
        this.referenceCount = 0;
        this.isSuspended = false;
        this.permission = 'unknown';
        this.emitter = typeof window !== 'undefined' ? new EventTarget() : { addEventListener(){}, removeEventListener(){}, dispatchEvent(){} };

        // Event listeners
        this.visibilityChangeHandler = this.handleVisibilityChange.bind(this);
        this.pageShowHandler = this.handlePageShow.bind(this);
        this.pageHideHandler = this.handlePageHide.bind(this);

        // Setup event listeners
        this.setupEventListeners();

        if (process.env.NODE_ENV === 'development') console.log('🎤 MicManager: Singleton initialized');
    }

    /**
     * Setup event listeners for tab visibility changes
     */
    setupEventListeners() {
        if (typeof document !== 'undefined') {
            document.addEventListener('visibilitychange', this.visibilityChangeHandler);
            window.addEventListener('pageshow', this.pageShowHandler);
            window.addEventListener('pagehide', this.pageHideHandler);
        }
    }

    /**
     * Handle tab visibility changes
     */
    handleVisibilityChange() {
        if (document.hidden) {
            this.suspendAudioContext();
        } else {
            this.resumeAudioContext();
        }
    }

    /**
     * Handle page show event
     */
    handlePageShow() {
        this.resumeAudioContext();
    }

    /**
     * Handle page hide event
     */
    handlePageHide() {
        this.suspendAudioContext();
    }

    /**
     * Get microphone permission state
     */
    async getPermission() {
        try {
            if (navigator.permissions && navigator.permissions.query) {
                const perm = await navigator.permissions.query({ name: 'microphone' });
                this.permission = perm.state === 'granted' ? 'granted' : perm.state === 'denied' ? 'denied' : 'prompt';
            } else {
                this.permission = 'prompt';
            }
        } catch (_) {
            this.permission = 'prompt';
        }
        try { this.emitter.dispatchEvent(new CustomEvent('permissionchange', { detail: this.permission })); } catch (_) {}
        return this.permission;
    }

    /**
     * Request microphone permission with a disposable stream
     */
    async requestPermission(constraints = { audio: true }) {
        try {
            const s = await navigator.mediaDevices.getUserMedia(constraints);
            try { s.getTracks().forEach(t => t.stop()); } catch (_) {}
            this.permission = 'granted';
        } catch (_) {
            this.permission = 'denied';
        }
        try { this.emitter.dispatchEvent(new CustomEvent('permissionchange', { detail: this.permission })); } catch (_) {}
        return this.permission;
    }

    /**
     * Preflight permission prompt without refresh
     */
    async preflight() {
        const p = await this.getPermission();
        if (p === 'prompt') {
            return this.requestPermission();
        }
        return p;
    }

    /**
     * Acquire microphone access
     * @param {Object} options - MediaStream constraints
     * @returns {Promise<MediaStream>} The acquired MediaStream
     */
    async acquire(options = {}) {
        // If already acquired, return existing stream
        if (this.isAcquired && this.mediaStream) {
            this.referenceCount++;
            if (process.env.NODE_ENV === 'development') console.log(`🎤 MicManager: Stream already acquired, reference count: ${this.referenceCount}`);
            try { this.emitter.dispatchEvent(new Event('acquired')); } catch (_) {}
            return this.mediaStream;
        }

        // If acquisition is in progress, wait for it
        if (this.acquisitionPromise) {
            console.log('🎤 MicManager: Waiting for existing acquisition...');
            return await this.acquisitionPromise;
        }

        // Start new acquisition
        this.acquisitionPromise = this.performAcquisition(options);
        
        try {
            const stream = await this.acquisitionPromise;
            this.referenceCount = 1;
            this.isAcquired = true;
            console.log('🎤 MicManager: Stream acquired successfully');
            try { this.emitter.dispatchEvent(new Event('acquired')); } catch (_) {}
            return stream;
        } finally {
            this.acquisitionPromise = null;
        }
    }

    /**
     * Perform the actual microphone acquisition
     * @param {Object} options - MediaStream constraints
     * @returns {Promise<MediaStream>} The acquired MediaStream
     */
    async performAcquisition(options = {}) {
        const constraints = {
            audio: {
                echoCancellation: true,
                noiseSuppression: true,
                autoGainControl: true,
                sampleRate: 16000,
                ...options.audio
            },
            ...options
        };

        if (process.env.NODE_ENV === 'development') console.log('🎤 MicManager: Requesting microphone access...', constraints);

        try {
            // Request microphone access
            const stream = await navigator.mediaDevices.getUserMedia(constraints);
            
            // Validate stream
            if (!stream) {
                throw new Error('No MediaStream returned from getUserMedia');
            }

            // Validate audio tracks
            const audioTracks = stream.getAudioTracks();
            if (audioTracks.length === 0) {
                throw new Error('No audio tracks in MediaStream');
            }

            // Check if tracks are live
            const liveTracks = audioTracks.filter(track => track.readyState === 'live');
            if (liveTracks.length === 0) {
                throw new Error('No live audio tracks in MediaStream');
            }

            if (process.env.NODE_ENV === 'development') console.log(`🎤 MicManager: MediaStream acquired with ${liveTracks.length} live audio tracks`);

            // Store the stream
            this.mediaStream = stream;

            // Create AudioContext if needed
            await this.ensureAudioContext();
            await this.resume();

            return stream;
        } catch (error) {
            console.error('🎤 MicManager: Failed to acquire microphone:', error);
            throw new Error(`Microphone access denied: ${error.message}`);
        }
    }

    /**
     * Ensure AudioContext exists and is in the correct state
     * @returns {Promise<AudioContext>} The AudioContext
     */
    async ensureAudioContext() {
        if (!this.audioContext) {
            console.log('🎤 MicManager: Creating AudioContext...');
            
            const AudioContextClass = window.AudioContext || window.webkitAudioContext;
            if (!AudioContextClass) {
                throw new Error('AudioContext not supported in this browser');
            }

            this.audioContext = new AudioContextClass({
                sampleRate: 16000,
                latencyHint: 'interactive'
            });

            console.log(`🎤 MicManager: AudioContext created, state: ${this.audioContext.state}`);
        }

        // Resume AudioContext if suspended
        if (this.audioContext.state === 'suspended') {
            console.log('🎤 MicManager: Resuming suspended AudioContext...');
            await this.audioContext.resume();
        }

        return this.audioContext;
    }

    /**
     * Release microphone access
     * @param {boolean} force - Force release even if other references exist
     */
    release(force = false) {
        if (!this.isAcquired) {
            console.log('🎤 MicManager: No stream to release');
            return;
        }

        this.referenceCount = Math.max(0, this.referenceCount - 1);
        console.log(`🎤 MicManager: Release requested, reference count: ${this.referenceCount}`);

        if (force || this.referenceCount === 0) {
            this.performRelease();
        }
    }

    /**
     * Release all microphone resources (force refCount to 0)
     * @returns {Promise<void>}
     */
    async releaseAll() {
        console.log('🎤 MicManager: Release all requested, current reference count:', this.referenceCount);
        
        // Force reference count to 0
        this.referenceCount = 0;
        
        // Stop all tracks
        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach(track => {
                console.log('🎤 MicManager: Stopped track:', track.kind);
                track.stop();
            });
            this.mediaStream = null;
        }
        
        // Suspend AudioContext (don't close - MicManager owns it)
        if (this.audioContext && this.audioContext.state !== 'closed') {
            await this.audioContext.suspend();
            console.log('🎤 MicManager: AudioContext suspended');
        }
        
        this.isAcquired = false;
        console.log('🎤 MicManager: Release all completed - refCount: 0, tracks stopped, AC suspended');
        try { this.emitter.dispatchEvent(new Event('released')); } catch (_) {}
    }

    /**
     * Resume AudioContext if suspended
     * @returns {Promise<void>}
     */
    async resumeIfSuspended() {
        if (this.audioContext && this.audioContext.state === 'suspended') {
            await this.audioContext.resume();
            console.log('🎤 MicManager: AudioContext resumed');
        }
    }

    /**
     * Perform the actual release
     */
    performRelease() {
        console.log('🎤 MicManager: Releasing microphone and AudioContext...');

        // Stop all audio tracks
        if (this.mediaStream) {
            this.mediaStream.getTracks().forEach(track => {
                track.stop();
                console.log(`🎤 MicManager: Stopped track: ${track.kind}`);
            });
            this.mediaStream = null;
        }

        // Suspend AudioContext instead of closing
        if (this.audioContext) {
            if (this.audioContext.state === 'running') {
                this.audioContext.suspend().catch(() => {});
            }
        }

        this.isAcquired = false;
        this.referenceCount = 0;
        this.isSuspended = false;

        console.log('🎤 MicManager: Release completed');
        try { this.emitter.dispatchEvent(new Event('released')); } catch (_) {}
    }

    /**
     * Suspend AudioContext
     */
    async suspendAudioContext() {
        if (this.audioContext && this.audioContext.state === 'running') {
            console.log('🎤 MicManager: Suspending AudioContext...');
            await this.audioContext.suspend();
            this.isSuspended = true;
            console.log('🎤 MicManager: AudioContext suspended');
        }
    }

    /**
     * Resume AudioContext
     */
    async resumeAudioContext() {
        if (this.audioContext && this.audioContext.state === 'suspended') {
            console.log('🎤 MicManager: Resuming AudioContext...');
            await this.audioContext.resume();
            this.isSuspended = false;
            console.log('🎤 MicManager: AudioContext resumed');
        }
    }

    /**
     * Resume helper (public): resumes AC if suspended
     */
    async resume() {
        await this.resumeAudioContext();
        return this.audioContext?.state || 'none';
    }

    /**
     * Get the current MediaStream
     * @returns {MediaStream|null} The current MediaStream
     */
    getMediaStream() {
        return this.mediaStream;
    }

    /**
     * Get the current AudioContext
     * @returns {AudioContext|null} The current AudioContext
     */
    getAudioContext() {
        return this.audioContext;
    }

    /**
     * Check if microphone is acquired
     * @returns {boolean} True if acquired
     */
    isMicrophoneAcquired() {
        return this.isAcquired && this.mediaStream !== null;
    }

    /**
     * Check if AudioContext is available
     * @returns {boolean} True if available
     */
    isAudioContextAvailable() {
        return this.audioContext !== null && this.audioContext.state !== 'closed';
    }

    /**
     * Get current state information
     * @returns {Object} State information
     */
    getState() {
        return {
            isAcquired: this.isAcquired,
            referenceCount: this.referenceCount,
            hasMediaStream: !!this.mediaStream,
            hasAudioContext: !!this.audioContext,
            audioContextState: this.audioContext?.state || 'none',
            isSuspended: this.isSuspended,
            trackCount: this.mediaStream?.getAudioTracks().length || 0,
            liveTrackCount: this.mediaStream?.getAudioTracks().filter(t => t.readyState === 'live').length || 0,
            permission: this.permission
        };
    }

    /**
     * Validate that the current setup is working
     * @returns {boolean} True if valid
     */
    validate() {
        if (!this.isAcquired) {
            console.warn('🎤 MicManager: Not acquired');
            return false;
        }

        if (!this.mediaStream) {
            console.warn('🎤 MicManager: No MediaStream');
            return false;
        }

        const audioTracks = this.mediaStream.getAudioTracks();
        if (audioTracks.length === 0) {
            console.warn('🎤 MicManager: No audio tracks');
            return false;
        }

        const liveTracks = audioTracks.filter(track => track.readyState === 'live');
        if (liveTracks.length === 0) {
            console.warn('🎤 MicManager: No live audio tracks');
            return false;
        }

        if (!this.audioContext) {
            console.warn('🎤 MicManager: No AudioContext');
            return false;
        }

        if (this.audioContext.state === 'closed') {
            console.warn('🎤 MicManager: AudioContext is closed');
            return false;
        }

        console.log('🎤 MicManager: Validation passed');
        return true;
    }

    /**
     * Create a MediaStreamSource for the current stream
     * @returns {MediaStreamAudioSourceNode} The source node
     */
    createMediaStreamSource() {
        if (!this.audioContext) {
            throw new Error('AudioContext not available');
        }

        if (!this.mediaStream) {
            throw new Error('MediaStream not available');
        }

        console.log('🎤 MicManager: Creating MediaStreamSource...');
        const source = this.audioContext.createMediaStreamSource(this.mediaStream);
        console.log('🎤 MicManager: MediaStreamSource created successfully');
        return source;
    }

    /**
     * Cleanup and destroy the singleton
     */
    destroy() {
        console.log('🎤 MicManager: Destroying singleton...');
        
        // Remove event listeners
        if (typeof document !== 'undefined') {
            document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
            window.removeEventListener('pageshow', this.pageShowHandler);
            window.removeEventListener('pagehide', this.pageHideHandler);
        }

        // Force release everything
        this.performRelease();

        // Clear singleton instance
        MicManager.instance = null;
        
        console.log('🎤 MicManager: Singleton destroyed');
    }

    on(type, handler) { try { this.emitter.addEventListener(type, handler); } catch (_) {} }
    off(type, handler) { try { this.emitter.removeEventListener(type, handler); } catch (_) {} }
}

// Create and export the singleton instance
const micManager = new MicManager();
export default micManager;
